"use client";

import { useEffect } from "react";
import { useMotionValue, useScroll, useSpring } from "motion/react";
import { usePrefersReducedMotion } from "@/lib/animation";

interface LogoSizes {
  /** Initial size at scroll = 0. */
  from: number;
  /** Final size after the scroll range is consumed. */
  to: number;
}

const LOGO: { mobile: LogoSizes; desktop: LogoSizes } = {
  mobile: { from: 120, to: 96 },
  desktop: { from: 168, to: 100 },
};

const CIRCLE: { mobile: LogoSizes } = {
  mobile: { from: 136, to: 104 },
};

/** Distance (px) over which the collapse plays out. */
const RANGE = { mobile: 200, desktop: 140 };

/**
 * Spring that smooths the raw scroll-derived progress. This is the
 * Motion equivalent of GSAP ScrollTrigger's `scrub` lerp — it's what
 * makes the collapse glide instead of sticking frame-perfect to the
 * scrollbar. Lower stiffness / higher damping = more lag, smoother.
 */
const SCRUB_SPRING = { stiffness: 120, damping: 30, mass: 0.4 } as const;

const lerp = (from: number, to: number, t: number) => from + (to - from) * t;

/**
 * Drives the header logo shrink-on-scroll with Motion.
 *
 * `useScroll` gives the raw scroll position; we map it to a 0→1 progress
 * over `RANGE`, smooth that through a spring (`SCRUB_SPRING`), and write
 * the interpolated width/height onto the logo and circle imperatively —
 * the same model the old GSAP version used, minus a second animation
 * library. Sizes differ per breakpoint, and the circle only resizes on
 * mobile (desktop lets CSS keep it `size-auto`).
 *
 * Honours `prefers-reduced-motion`: in that mode the hook does nothing
 * and the static sizes from the `.nav-logo` / `.nav-logo-circle`
 * reduced-motion CSS fallback take over.
 */
export function useLogoScrollAnimation(
  logoRef: React.RefObject<HTMLImageElement | null>,
  circleRef: React.RefObject<HTMLAnchorElement | null>,
) {
  const reduced = usePrefersReducedMotion();
  const { scrollY } = useScroll();
  const progress = useMotionValue(0);
  const smoothed = useSpring(progress, SCRUB_SPRING);

  useEffect(() => {
    const logo = logoRef.current;
    const circle = circleRef.current;
    if (!logo || !circle) return;

    if (reduced) {
      // Drop any inline sizes a pre-detection frame may have written so the
      // static `.nav-logo` / `.nav-logo-circle` reduced-motion CSS wins.
      logo.style.width = "";
      logo.style.height = "";
      circle.style.width = "";
      circle.style.height = "";
      return;
    }

    const isDesktop = () => window.matchMedia("(min-width: 1280px)").matches;

    const apply = (t: number) => {
      const desktop = isDesktop();
      const logoFrom = (desktop ? LOGO.desktop : LOGO.mobile).from;
      const logoTo = (desktop ? LOGO.desktop : LOGO.mobile).to;
      const logoSize = lerp(logoFrom, logoTo, t);
      logo.style.width = `${logoSize}px`;
      logo.style.height = `${logoSize}px`;

      if (desktop) {
        // Desktop circle stays CSS-driven (`size-auto`).
        circle.style.width = "";
        circle.style.height = "";
      } else {
        const circleSize = lerp(CIRCLE.mobile.from, CIRCLE.mobile.to, t);
        circle.style.width = `${circleSize}px`;
        circle.style.height = `${circleSize}px`;
      }
    };

    const syncProgress = () => {
      const range = isDesktop() ? RANGE.desktop : RANGE.mobile;
      progress.set(Math.min(1, Math.max(0, scrollY.get() / range)));
    };

    // Seed the from-sizes and the current scroll position immediately.
    apply(smoothed.get());
    syncProgress();

    const unsubScroll = scrollY.on("change", syncProgress);
    const unsubSmoothed = smoothed.on("change", apply);
    window.addEventListener("resize", syncProgress);

    return () => {
      unsubScroll();
      unsubSmoothed();
      window.removeEventListener("resize", syncProgress);
    };
  }, [logoRef, circleRef, reduced, scrollY, progress, smoothed]);
}
